import { fetchWithSupabaseAuth, supabase } from "./supabaseClient.js";

export const SOURCE_FILES_STORAGE_BUCKET = "sources";
export const SOURCE_FILES_STORAGE_PROVIDER_LABELS = {
  backend: "Stockage serveur",
  supabase: "Supabase Storage",
};

const DEFAULT_SOURCE_FILES_STORAGE_MODE = "backend";
const SOURCE_FILES_STORAGE_BACKEND_API_ROOT = "/api/import/source-files";

export const SOURCE_FILE_STORAGE_CONFIGS = {
  orders: {
    downloadBaseName: "orders",
    field: "orders",
    label: "Orders",
    objectPath: "orders/current",
  },
  trucks: {
    downloadBaseName: "trucks",
    field: "trucks",
    label: "Trucks",
    objectPath: "trucks/current",
  },
  compartments: {
    downloadBaseName: "repartition-citernes",
    field: "compartments",
    label: "Remorques",
    objectPath: "repartition-citernes/current",
  },
  employees: {
    downloadBaseName: "data-manager-chauffeurs-export",
    field: "employees",
    label: "Chauffeurs",
    objectPath: "employees/current",
  },
  client_correspondences: {
    downloadBaseName: "client-correspondences",
    field: "client_correspondences",
    label: "Clients",
    objectPath: "client-correspondences/current",
  },
  products: {
    downloadBaseName: "data-manager-products-export",
    field: "products",
    label: "Produits",
    objectPath: "product-correspondences/current",
  },
};

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

const sourceFilesImportMetaEnv = typeof import.meta === "object"
  && import.meta
  && typeof import.meta.env === "object"
  && import.meta.env
  ? import.meta.env
  : {};
const configuredSourceFilesStorageMode = (() => {
  const rawValue = typeof sourceFilesImportMetaEnv.VITE_SOURCE_FILES_STORAGE_MODE === "string"
    ? sourceFilesImportMetaEnv.VITE_SOURCE_FILES_STORAGE_MODE
    : typeof sourceFilesImportMetaEnv.VITE_SOURCE_FILES_STORAGE_PROVIDER === "string"
      ? sourceFilesImportMetaEnv.VITE_SOURCE_FILES_STORAGE_PROVIDER
      : "";
  const normalizedValue = rawValue.trim().toLowerCase();
  return ["auto", "backend", "supabase"].includes(normalizedValue)
    ? normalizedValue
    : DEFAULT_SOURCE_FILES_STORAGE_MODE;
})();
let runtimeSourceFilesStorageProvider = configuredSourceFilesStorageMode === "backend"
  || (configuredSourceFilesStorageMode !== "supabase" && !supabase)
  ? "backend"
  : "supabase";

function resolveStorageConfig(sourceKey) {
  const config = SOURCE_FILE_STORAGE_CONFIGS[sourceKey];
  if (!config) {
    throw new Error(`Source Storage inconnue: ${sourceKey || "non renseignée"}.`);
  }

  return config;
}

function splitStorageObjectPath(objectPath) {
  const parts = String(objectPath || "")
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  const objectName = parts.at(-1) || "";

  return {
    directoryPath: parts.slice(0, -1).join("/"),
    objectName,
  };
}

function resolveSpreadsheetExtension(mimeType) {
  const normalizedMimeType = typeof mimeType === "string"
    ? mimeType.trim().toLowerCase()
    : "";

  if (normalizedMimeType.includes("spreadsheetml")) {
    return ".xlsx";
  }

  if (normalizedMimeType.includes("vnd.ms-excel")) {
    return ".xls";
  }

  return ".xlsx";
}

function resolveDownloadFileName(config, mimeType) {
  return `${config.downloadBaseName}${resolveSpreadsheetExtension(mimeType)}`;
}

function normalizeStorageMetadata(fileObject) {
  return isPlainObject(fileObject?.metadata) ? fileObject.metadata : {};
}

function resolveContentType(fileObject) {
  const metadata = normalizeStorageMetadata(fileObject);
  return typeof metadata.mimetype === "string" && metadata.mimetype.trim()
    ? metadata.mimetype.trim()
    : "";
}

function resolveSizeBytes(fileObject) {
  const metadata = normalizeStorageMetadata(fileObject);
  if (typeof metadata.size === "number" && Number.isFinite(metadata.size)) {
    return metadata.size;
  }

  if (typeof metadata.size === "string" && metadata.size.trim()) {
    const parsedSize = Number(metadata.size);
    if (Number.isFinite(parsedSize)) {
      return parsedSize;
    }
  }

  return null;
}

function triggerBrowserDownload(blob, fileName) {
  if (!(blob instanceof Blob)) {
    throw new Error("Téléchargement indisponible: aucun fichier binaire n'a été reçu.");
  }

  if (typeof window === "undefined" || !window.URL || !window.document?.body) {
    throw new Error("Téléchargement indisponible dans cet environnement.");
  }

  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = "noopener";
  window.document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl);
  }, 120000);
}

function resolveSourceFilesStorageRuntimeLabel(provider) {
  return SOURCE_FILES_STORAGE_PROVIDER_LABELS[provider] || SOURCE_FILES_STORAGE_PROVIDER_LABELS.supabase;
}

function setRuntimeSourceFilesStorageProvider(provider) {
  if (provider !== "backend" && provider !== "supabase") {
    return;
  }

  runtimeSourceFilesStorageProvider = provider;
}

function getPreferredSourceFilesStorageProviders() {
  if (configuredSourceFilesStorageMode === "backend") {
    return ["backend"];
  }

  if (configuredSourceFilesStorageMode === "supabase") {
    return ["supabase"];
  }

  if (runtimeSourceFilesStorageProvider === "backend" || !supabase) {
    return ["backend"];
  }

  return ["supabase", "backend"];
}

function resolveResponseErrorMessage(payload, fallbackMessage) {
  if (typeof payload?.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }

  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  return fallbackMessage;
}

async function parseSourceFilesBackendError(response, fallbackMessage) {
  const rawText = await response.text();
  let payload = null;

  try {
    payload = rawText ? JSON.parse(rawText) : null;
  } catch {
    payload = null;
  }

  const error = new Error(resolveResponseErrorMessage(payload, fallbackMessage));
  error.status = response.status;
  error.payload = payload;
  return error;
}

function isMissingSupabaseBucketError(error) {
  const statusCode = typeof error?.status === "number"
    ? error.status
    : typeof error?.statusCode === "string" && error.statusCode.trim()
      ? Number(error.statusCode)
      : null;
  const message = typeof error?.message === "string"
    ? error.message.trim().toLowerCase()
    : "";

  return statusCode === 404
    || message.includes("bucket not found")
    || message.includes("bucket missing")
    || message.includes("bucket inconnu");
}

async function runSourceFilesStorageOperation(handlers) {
  let lastError = null;

  for (const provider of getPreferredSourceFilesStorageProviders()) {
    const handler = handlers?.[provider];
    if (typeof handler !== "function") {
      continue;
    }

    try {
      const result = await handler();
      setRuntimeSourceFilesStorageProvider(provider);
      return result;
    } catch (error) {
      if (provider === "supabase" && configuredSourceFilesStorageMode === "auto" && isMissingSupabaseBucketError(error)) {
        lastError = error;
        setRuntimeSourceFilesStorageProvider("backend");
        continue;
      }

      throw error;
    }
  }

  if (lastError) {
    throw lastError;
  }

  throw new Error("Aucun stockage de fichiers sources n'est configuré.");
}

async function fetchSourceFilesBackendJson(path, init, fallbackMessage) {
  const response = await fetchWithSupabaseAuth(path, init);
  if (!response.ok) {
    throw await parseSourceFilesBackendError(response, fallbackMessage);
  }

  const payload = await response.json();
  setRuntimeSourceFilesStorageProvider("backend");
  return payload;
}

export async function resolveSourceFilesManagementAccessFromBackend() {
  const response = await fetchWithSupabaseAuth(
    `${SOURCE_FILES_STORAGE_BACKEND_API_ROOT}/access`,
    { method: "GET" },
  );

  if (!response.ok) {
    throw await parseSourceFilesBackendError(
      response,
      "Les droits de gestion des fichiers courants n'ont pas pu être vérifiés.",
    );
  }

  const payload = await response.json();
  return typeof payload?.can_manage === "boolean"
    ? payload.can_manage
    : null;
}

function buildStoredSourceFileRecord(sourceKey, fileObject) {
  const config = resolveStorageConfig(sourceKey);
  const contentType = resolveContentType(fileObject);

  return {
    bucket: SOURCE_FILES_STORAGE_BUCKET,
    contentType,
    downloadFileName: resolveDownloadFileName(config, contentType),
    field: config.field,
    label: config.label,
    objectPath: config.objectPath,
    pathLabel: resolveSourceStoragePathLabel(sourceKey),
    provider: "supabase",
    providerLabel: resolveSourceFilesStorageRuntimeLabel("supabase"),
    sizeBytes: resolveSizeBytes(fileObject),
    updatedAt: fileObject?.updated_at || fileObject?.created_at || "",
  };
}

async function resolveCurrentSourceFileFromSupabase(sourceKey) {
  const config = resolveStorageConfig(sourceKey);
  if (!supabase) {
    throw new Error("Supabase Storage n'est pas configuré dans ce frontend.");
  }

  const { directoryPath, objectName } = splitStorageObjectPath(config.objectPath);
  const { data, error } = await supabase.storage
    .from(SOURCE_FILES_STORAGE_BUCKET)
    .list(directoryPath, { limit: 100 });

  if (error) {
    throw error;
  }

  const currentFileObject = Array.isArray(data)
    ? data.find((entry) => entry?.name === objectName)
    : null;

  return currentFileObject
    ? buildStoredSourceFileRecord(sourceKey, currentFileObject)
    : null;
}

async function downloadCurrentSourceFileFromSupabase(sourceKey, currentFile = null) {
  const config = resolveStorageConfig(sourceKey);
  if (!supabase) {
    throw new Error("Supabase Storage n'est pas configuré dans ce frontend.");
  }

  const { data, error } = await supabase.storage
    .from(SOURCE_FILES_STORAGE_BUCKET)
    .download(config.objectPath);

  if (error) {
    throw error;
  }

  const fileName = typeof currentFile?.downloadFileName === "string" && currentFile.downloadFileName.trim()
    ? currentFile.downloadFileName.trim()
    : resolveDownloadFileName(config, data?.type || "");

  triggerBrowserDownload(data, fileName);

  return {
    fileName,
    pathLabel: `${SOURCE_FILES_STORAGE_BUCKET}/${config.objectPath}`,
  };
}

async function uploadCurrentSourceFileFromSupabase(sourceKey, file) {
  const config = resolveStorageConfig(sourceKey);
  if (!supabase) {
    throw new Error("Supabase Storage n'est pas configuré dans ce frontend.");
  }

  if (!(file instanceof File)) {
    throw new Error("Aucun fichier local n'a été sélectionné.");
  }

  const { error } = await supabase.storage
    .from(SOURCE_FILES_STORAGE_BUCKET)
    .upload(config.objectPath, file, {
      cacheControl: "0",
      contentType: file.type || undefined,
      upsert: true,
    });

  if (error) {
    throw error;
  }

  return {
    fileName: file.name,
    pathLabel: `${SOURCE_FILES_STORAGE_BUCKET}/${config.objectPath}`,
  };
}

async function deleteCurrentSourceFileFromSupabase(sourceKey) {
  const config = resolveStorageConfig(sourceKey);
  if (!supabase) {
    throw new Error("Supabase Storage n'est pas configuré dans ce frontend.");
  }

  const { error } = await supabase.storage
    .from(SOURCE_FILES_STORAGE_BUCKET)
    .remove([config.objectPath]);

  if (error) {
    throw error;
  }

  return {
    pathLabel: `${SOURCE_FILES_STORAGE_BUCKET}/${config.objectPath}`,
  };
}

async function resolveCurrentSourceFileFromBackend(sourceKey) {
  resolveStorageConfig(sourceKey);
  const payload = await fetchSourceFilesBackendJson(
    `${SOURCE_FILES_STORAGE_BACKEND_API_ROOT}/${encodeURIComponent(sourceKey)}`,
    { method: "GET" },
    "Le fichier courant n'a pas pu être résolu depuis le stockage serveur.",
  );
  return {
    canManage: typeof payload?.can_manage === "boolean" ? payload.can_manage : null,
    currentFile: payload?.current_file || null,
  };
}

async function downloadCurrentSourceFileFromBackend(sourceKey, currentFile = null) {
  const config = resolveStorageConfig(sourceKey);
  const response = await fetchWithSupabaseAuth(
    `${SOURCE_FILES_STORAGE_BACKEND_API_ROOT}/${encodeURIComponent(sourceKey)}/download`,
    { method: "GET" },
  );
  if (!response.ok) {
    throw await parseSourceFilesBackendError(response, "Le téléchargement du fichier courant a échoué.");
  }

  const blob = await response.blob();
  const fileName = typeof currentFile?.downloadFileName === "string" && currentFile.downloadFileName.trim()
    ? currentFile.downloadFileName.trim()
    : resolveDownloadFileName(config, blob.type || "");
  triggerBrowserDownload(blob, fileName);
  setRuntimeSourceFilesStorageProvider("backend");
  return {
    fileName,
    pathLabel: resolveSourceStoragePathLabel(sourceKey),
  };
}

async function uploadCurrentSourceFileFromBackend(sourceKey, file) {
  resolveStorageConfig(sourceKey);
  if (!(file instanceof File)) {
    throw new Error("Aucun fichier local n'a été sélectionné.");
  }

  const formData = new FormData();
  formData.set("file", file);
  const payload = await fetchSourceFilesBackendJson(
    `${SOURCE_FILES_STORAGE_BACKEND_API_ROOT}/${encodeURIComponent(sourceKey)}`,
    {
      method: "POST",
      body: formData,
    },
    "Le dépôt du fichier courant a échoué.",
  );
  const currentFile = payload?.current_file;
  return {
    fileName: currentFile?.downloadFileName || file.name,
    pathLabel: currentFile?.pathLabel || resolveSourceStoragePathLabel(sourceKey),
  };
}

async function deleteCurrentSourceFileFromBackend(sourceKey) {
  resolveStorageConfig(sourceKey);
  const payload = await fetchSourceFilesBackendJson(
    `${SOURCE_FILES_STORAGE_BACKEND_API_ROOT}/${encodeURIComponent(sourceKey)}`,
    { method: "DELETE" },
    "La suppression du fichier courant a échoué.",
  );
  return {
    pathLabel: typeof payload?.path_label === "string" && payload.path_label.trim()
      ? payload.path_label.trim()
      : resolveSourceStoragePathLabel(sourceKey),
  };
}

export function isSourceFilesStorageConfigured() {
  return configuredSourceFilesStorageMode !== "supabase" || Boolean(supabase);
}

export function getSourceFilesStorageRuntimeInfo() {
  return {
    kind: runtimeSourceFilesStorageProvider,
    label: resolveSourceFilesStorageRuntimeLabel(runtimeSourceFilesStorageProvider),
    mode: configuredSourceFilesStorageMode,
  };
}

export function resolveSourceStoragePathLabel(sourceKey) {
  const config = resolveStorageConfig(sourceKey);
  if (runtimeSourceFilesStorageProvider === "backend") {
    return `serveur/${config.field}/courant`;
  }
  return `${SOURCE_FILES_STORAGE_BUCKET}/${config.objectPath}`;
}

export async function resolveCurrentSourceFile(sourceKey) {
  const result = await runSourceFilesStorageOperation({
    backend: () => resolveCurrentSourceFileFromBackend(sourceKey),
    supabase: async () => ({
      canManage: null,
      currentFile: await resolveCurrentSourceFileFromSupabase(sourceKey),
    }),
  });
  return {
    canManage: typeof result?.canManage === "boolean" ? result.canManage : null,
    currentFile: result?.currentFile || null,
  };
}

export async function downloadCurrentSourceFile(sourceKey, currentFile = null) {
  return runSourceFilesStorageOperation({
    backend: () => downloadCurrentSourceFileFromBackend(sourceKey, currentFile),
    supabase: () => downloadCurrentSourceFileFromSupabase(sourceKey, currentFile),
  });
}

export async function uploadCurrentSourceFile(sourceKey, file) {
  return runSourceFilesStorageOperation({
    backend: () => uploadCurrentSourceFileFromBackend(sourceKey, file),
    supabase: () => uploadCurrentSourceFileFromSupabase(sourceKey, file),
  });
}

export async function deleteCurrentSourceFile(sourceKey) {
  return runSourceFilesStorageOperation({
    backend: () => deleteCurrentSourceFileFromBackend(sourceKey),
    supabase: () => deleteCurrentSourceFileFromSupabase(sourceKey),
  });
}
